import type { ObjectId } from 'mongodb';

import type { AccountMilestoneStatus } from './AccountMilestones';
import type { MilestoneType } from './Milestones';
import type { ProgramType } from './ProgramTypes';

export enum AgingGroup {
  thirtyToOneHundredSixtyDays = 'thirtyToOneHundredSixtyDays',
  oneToThirtyDays = 'oneToThirtyDays',
}

export enum AgingQuery {
  ntpWithoutInstall = 'ntpWithoutInstall',
  installApprovedWithoutActivationApproved = 'installApprovedWithoutActivationApproved',
  invoiceWithoutInstallSubmitted = 'invoiceWithoutInstallSubmitted',
  installSubmittedWithoutInvoice = 'installSubmittedWithoutInvoice',
  installConditionalApprovalWithoutActivationSubmitted = 'installConditionalApprovalWithoutActivationSubmitted',
  installRejected = 'installRejected',
  installSubmittedWithoutActivationApproved = 'installSubmittedWithoutActivationApproved',
  activationRejectedWithoutActivationResubmitted = 'activationRejectedWithoutActivationResubmitted',
}

export enum ThirtyToOneHundredSixtyDaysAgingEnum {
  lessThanOrEqualTo30 = 'lessThanOrEqualTo30',
  '31to60' = '31to60',
  '61to90' = '61to90',
  '91to115' = '91to115',
  '116to160' = '116to160',
  greaterThan160 = 'greaterThan160',
}

export enum OneToThirtyDaysAgingEnum {
  lessThanOrEqualTo1 = 'lessThanOrEqualTo1',
  '2to3' = '2to3',
  '4to6' = '4to6',
  '7to10' = '7to10',
  '11to14' = '11to14',
  '15to20' = '15to20',
  '21to30' = '21to30',
  greaterThan30 = 'greaterThan30',
}

export type AgingBucket = ThirtyToOneHundredSixtyDaysAgingEnum | OneToThirtyDaysAgingEnum;

type DateInfo = {
  earliestDate: Date;
  latestDate: Date;
};

type MilestoneStatus = Partial<Record<AccountMilestoneStatus, DateInfo>>;

type MilestoneStatusMap = Partial<Record<MilestoneType, MilestoneStatus & { status: AccountMilestoneStatus }>>;

export type AgingViewDocument = {
  _id: ObjectId;
  accountId: ObjectId;
  organizationId: string;
  programType: ProgramType;
  distributor: {
    earliestInvoiceDate: Date;
    latestInvoiceDate: Date;
    shipConfirmationDate: Date;
  };
} & MilestoneStatusMap;

export interface AgingMetric {
  accountData: Array<AgingAccountData>;
  value: number;
}

export interface AgingAccountData {
  accountId: string;
  daysAging: number;
}

export type ThirtyToOneHundredSixtyDays = {
  [K in ThirtyToOneHundredSixtyDaysAgingEnum]: AgingMetric;
};

export type OneToThirtyDays = {
  [K in OneToThirtyDaysAgingEnum]: AgingMetric;
};

export type AgingQueryFilter = {
  agingQuery: AgingQuery;
  agingBuckets: AgingBucket[];
};

export type AgingBucketData = {
  [K in AgingQuery]: AgingAccountData[];
};

export type DaysAgingSchema = Record<AgingQuery, { type: 'number'; minimum: number }>;

export const AgingQueryGroupMap: {
  [K in AgingQuery]: typeof ThirtyToOneHundredSixtyDaysAgingEnum | typeof OneToThirtyDaysAgingEnum;
} = {
  [AgingQuery.ntpWithoutInstall]: ThirtyToOneHundredSixtyDaysAgingEnum,
  [AgingQuery.installSubmittedWithoutActivationApproved]: ThirtyToOneHundredSixtyDaysAgingEnum,
  [AgingQuery.installApprovedWithoutActivationApproved]: ThirtyToOneHundredSixtyDaysAgingEnum,
  [AgingQuery.invoiceWithoutInstallSubmitted]: OneToThirtyDaysAgingEnum,
  [AgingQuery.installSubmittedWithoutInvoice]: OneToThirtyDaysAgingEnum,
  [AgingQuery.installConditionalApprovalWithoutActivationSubmitted]: OneToThirtyDaysAgingEnum,
  [AgingQuery.installRejected]: OneToThirtyDaysAgingEnum,
  [AgingQuery.activationRejectedWithoutActivationResubmitted]: OneToThirtyDaysAgingEnum,
} as const;

export const agingLabels: Record<
  AgingQuery | ThirtyToOneHundredSixtyDaysAgingEnum | OneToThirtyDaysAgingEnum,
  string
> = {
  [AgingQuery.ntpWithoutInstall]: 'NTP - New Homes',
  [AgingQuery.installApprovedWithoutActivationApproved]: 'Install Approved',
  [AgingQuery.invoiceWithoutInstallSubmitted]: 'Distributor Invoice Without Install Submitted',
  [AgingQuery.installSubmittedWithoutInvoice]: 'Install Submitted Without Distributor Invoice',
  [AgingQuery.installConditionalApprovalWithoutActivationSubmitted]: 'Conditional Approval',
  [AgingQuery.installRejected]: 'Install Rejected',
  [AgingQuery.installSubmittedWithoutActivationApproved]: 'Install Submitted',
  [AgingQuery.activationRejectedWithoutActivationResubmitted]: 'Activation Rejected',
  [ThirtyToOneHundredSixtyDaysAgingEnum.lessThanOrEqualTo30]: '<=30',
  [ThirtyToOneHundredSixtyDaysAgingEnum['31to60']]: '31-60',
  [ThirtyToOneHundredSixtyDaysAgingEnum['61to90']]: '61-90',
  [ThirtyToOneHundredSixtyDaysAgingEnum['91to115']]: '91-115',
  [ThirtyToOneHundredSixtyDaysAgingEnum['116to160']]: '116-160',
  [ThirtyToOneHundredSixtyDaysAgingEnum.greaterThan160]: '>160',
  [OneToThirtyDaysAgingEnum.lessThanOrEqualTo1]: '<=1',
  [OneToThirtyDaysAgingEnum['2to3']]: '2-3',
  [OneToThirtyDaysAgingEnum['4to6']]: '4-6',
  [OneToThirtyDaysAgingEnum['7to10']]: '7-10',
  [OneToThirtyDaysAgingEnum['11to14']]: '11-14',
  [OneToThirtyDaysAgingEnum['15to20']]: '15-20',
  [OneToThirtyDaysAgingEnum['21to30']]: '21-30',
  [OneToThirtyDaysAgingEnum.greaterThan30]: '>30',
};
